import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Card } from "@/components/Shared";
import { StatusPill } from "@/components/DocParts";
import { formatINR, formatDate } from "@/lib/format";
import { ChevronRight, Plus, Pencil, Check, X } from "lucide-react";

export const OBJ = {
  customer: { label: "Customer", plural: "Customers", color: "#7F8DE1", path: "/customers" },
  enquiry: { label: "Enquiry", plural: "Enquiries", color: "#F83A84", path: "/enquiries" },
  quote: { label: "Quote", plural: "Quotes", color: "#3B82F6", path: "/quotes" },
  order: { label: "Sales Order", plural: "Sales Orders", color: "#0B7C5A", path: "/orders" },
  invoice: { label: "Invoice", plural: "Invoices", color: "#D97706", path: "/invoices" },
  payment: { label: "Payment", plural: "Payments", color: "#0EA5E9", path: null },
  line: { label: "Line", plural: "Lines", color: "#6B7189", path: null },
};

export function ObjIcon({ kind, size = 9 }) {
  const o = OBJ[kind];
  return (
    <div className={`w-${size} h-${size} rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0`} style={{ background: o.color }}>
      {o.label[0]}
    </div>
  );
}

export function RecordHeader({ kind, title, status, subtitle, actions, breadcrumb }) {
  const navigate = useNavigate();
  const o = OBJ[kind];
  return (
    <div className="bg-white border border-[#E6E8F0] rounded-xl px-4 sm:px-5 py-3 mb-4 shadow-sm" data-testid="record-header">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <ObjIcon kind={kind} size={10} />
          <div className="min-w-0">
            <div className="text-xs text-[#6B7189] flex items-center gap-1">
              <button onClick={() => navigate(breadcrumb?.path || o.path)} className="hover:underline" data-testid="record-breadcrumb">{breadcrumb?.label || o.plural || `${o.label}s`}</button>
              <ChevronRight className="w-3 h-3" /><span>{o.label}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-[#101D51] truncate">{title}</h1>
              {status && <StatusPill status={status} testid="doc-status" />}
            </div>
            {subtitle && <div className="text-xs text-[#6B7189] truncate mt-0.5">{subtitle}</div>}
          </div>
        </div>
        {actions && <div className="flex flex-wrap gap-2 lg:justify-end" data-testid="doc-actions">{actions}</div>}
      </div>
    </div>
  );
}

export function Field({ label, value, link, onClick, mono, testid, edit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const v = value === null || value === undefined || value === "" ? "—" : value;
  const start = () => { setDraft(edit.raw ?? (typeof value === "string" ? value : "") ?? ""); setEditing(true); };
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/${edit.collection}/${edit.id}/field`, { field: edit.field, value: draft }); toast.success("Saved"); setEditing(false); edit.onSaved?.(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const inputCls = "h-8 w-full rounded-md border border-[#3B82F6] px-2 text-sm text-[#101D51] focus:outline-none";
  return (
    <div className="border-b border-[#F0F1F5] py-2 group" data-testid={testid || (edit ? `field-${edit.field}` : undefined)}>
      <div className="text-[11px] text-[#6B7189]">{label}</div>
      {editing ? (
        <div className="flex items-center gap-1 mt-0.5" data-testid={`edit-${edit.field}`}>
          {edit.type === "select"
            ? <select className={inputCls} value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={`edit-input-${edit.field}`}>{edit.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>
            : edit.type === "textarea"
              ? <textarea className={`${inputCls} h-20 py-1`} value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={`edit-input-${edit.field}`} />
              : <input type={edit.type || "text"} className={inputCls} value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus data-testid={`edit-input-${edit.field}`}
                onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }} />}
          <button onClick={save} disabled={saving} className="w-8 h-8 rounded-md bg-[#0B7C5A] text-white flex items-center justify-center shrink-0" data-testid={`edit-save-${edit.field}`}><Check className="w-4 h-4" /></button>
          <button onClick={() => setEditing(false)} className="w-8 h-8 rounded-md border border-[#E6E8F0] text-[#6B7189] flex items-center justify-center shrink-0" data-testid={`edit-cancel-${edit.field}`}><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          {link || onClick
            ? <button onClick={onClick} className="text-sm text-[#1D4ED8] hover:underline text-left font-medium break-words">{v}</button>
            : edit
              ? <button onClick={start} className={`text-sm text-[#101D51] break-words flex-1 text-left hover:text-[#1D4ED8] cursor-text ${mono ? "font-mono text-xs" : ""}`} title="Click to edit" data-testid={`edit-value-${edit.field}`}>{v}</button>
              : <div className={`text-sm text-[#101D51] break-words flex-1 ${mono ? "font-mono text-xs" : ""}`}>{v}</div>}
          {edit && <button onClick={start} className="opacity-40 group-hover:opacity-100 focus:opacity-100 text-[#6B7189] hover:text-[#101D51] shrink-0 mt-0.5 transition-opacity" title="Edit" data-testid={`edit-btn-${edit.field}`}><Pencil className="w-3.5 h-3.5" /></button>}
        </div>
      )}
    </div>
  );
}

export function DetailPanel({ title = "Details", children, className = "" }) {
  return (
    <Card className={`p-4 ${className}`} data-testid="detail-panel">
      <div className="text-xs font-semibold uppercase tracking-wide text-[#6B7189] mb-1">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-x-6">{children}</div>
    </Card>
  );
}

export function RecordLayout({ left, right }) {
  return (
    <div className="grid lg:grid-cols-3 gap-4 items-start">
      <div className="space-y-4 lg:sticky lg:top-4 min-w-0">{left}</div>
      <div className="lg:col-span-2 space-y-4 min-w-0">{right}</div>
    </div>
  );
}

export function RelatedList({ kind, title, rows, columns, onNew, newLabel = "New", emptyHint, testid, footer }) {
  const navigate = useNavigate();
  const o = OBJ[kind];
  const id = testid || `related-${(title || o.plural || `${o.label}s`).toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <Card className="overflow-hidden min-w-0" data-testid={id}>
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E6E8F0]">
        <ObjIcon kind={kind} size={8} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[#101D51]">{title || o.plural || `${o.label}s`} <span className="text-[#6B7189] font-normal">({rows.length})</span></div>
        </div>
        {onNew && <button onClick={onNew} className="h-8 px-3 rounded-lg border border-[#E6E8F0] text-xs font-medium text-[#101D51] hover:bg-[#F5F6FA] flex items-center gap-1" data-testid={`${id}-new`}><Plus className="w-3.5 h-3.5" /> {newLabel}</button>}
      </div>
      {rows.length === 0 ? <div className="px-4 py-5 text-sm text-[#6B7189]">{emptyHint || "No records."}</div> : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F5F6FA] text-[11px] uppercase tracking-wide text-[#6B7189]">
              <tr>{columns.map((c) => <th key={c.key} className={`px-3 py-2 font-medium text-left whitespace-nowrap ${c.right ? "text-right" : ""}`}>{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={r.path ? () => navigate(r.path) : undefined} data-testid={`row-${r.number || r.id}`}
                  className={`border-t border-[#F0F1F5] ${r.path ? "cursor-pointer hover:bg-[#F5F6FA]" : ""}`}>
                  {columns.map((c, i) => (
                    <td key={c.key} className={`px-3 py-2 whitespace-nowrap ${c.right ? "text-right" : ""} ${i === 0 && r.path ? "text-[#1D4ED8] font-medium" : "text-[#101D51]"}`}>
                      {c.render ? c.render(r) : r[c.key] ?? "—"}
                    </td>))}
                </tr>))}
            </tbody>
          </table>
        </div>
      )}
      {footer}
    </Card>
  );
}

export const col = {
  number: (label = "Number") => ({ key: "number", label }),
  status: () => ({ key: "status", label: "Status", render: (r) => <StatusPill status={r.display_status || r.status} /> }),
  date: (key = "date", label = "Date") => ({ key, label, render: (r) => formatDate(r[key]) }),
  money: (key = "grand_total", label = "Total") => ({ key, label, right: true, render: (r) => formatINR(r[key]) }),
  text: (key, label) => ({ key, label }),
};

export function paymentsFromInvoices(invoices) {
  return (invoices || []).flatMap((i) => (i.payments || []).map((p) => ({ ...p, invoice_number: i.number, path: `/invoices/${i.id}` })));
}
export const PAYMENT_COLS = [
  { key: "reference", label: "Reference", render: (p) => p.reference || p.mode },
  { key: "invoice_number", label: "Invoice" },
  { key: "mode", label: "Mode" },
  col.date("date", "Date"),
  col.money("amount", "Amount"),
];
